/**
 * The screen you get before a session exists.
 *
 * Clicking ＋ used to create a session, which meant the fleet filled with rows
 * called "New session" that nobody ever spoke to, and a name could only be
 * derived a turn later. Every other assistant opens an empty conversation and
 * commits nothing until you say something. This does the same, and the
 * difference is not cosmetic: **the session is created by the first message**,
 * so it is created already knowing where it runs, on what, with which model and
 * under which permission mode — and auto-naming, which keys off the default
 * title, names it from that first message with no change at all.
 *
 * The pre-flight moment is also the only sensible place to answer "where does
 * this run". Answering it afterwards, in a sheet, is answering it once the
 * question has stopped being interesting.
 *
 * Everything on this screen is local state until Send. Nothing is written, no
 * workspace is attached, no file is uploaded. That is what makes closing it
 * free, which is what makes opening it cheap.
 */

import { useEffect, useRef, useState } from 'react';
import type { PermissionMode } from '@chat42/runtime/modes';
import { useChat42, useChat42State } from './context.ts';
import { Composer, type Attached } from './composer.tsx';
import { ChipBar, type DraftPlace } from './chips.tsx';

/**
 * How long the box takes to reach the bottom.
 *
 * Shared with the stylesheet rather than guessed at twice: the navigation is
 * held for exactly this long, and a number here that disagreed with the CSS
 * would show as the composer jumping the last few pixels.
 */
export const SLIDE_MS = 420;

export interface NewSessionProps {
  /** Called with the id once the first message has made one. */
  onCreated: (id: string) => void;
}

export function NewSession({ onCreated }: NewSessionProps) {
  const { client } = useChat42();
  const state = useChat42State();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  /** Travelling to the bottom. Separate from `busy`, which is about the work. */
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  const [model, setModel] = useState<string | undefined>();
  // Undefined is the person's own default, which the runtime applies when a
  // session has no mode. Not Manual: see the mode chip in chips.tsx.
  const [mode, setMode] = useState<PermissionMode | undefined>(undefined);
  const [place, setPlace] = useState<DraftPlace>({ workspaceIds: [] });

  /**
   * Files, held rather than uploaded.
   *
   * There is nothing to upload them to yet, and creating a session on the first
   * attachment would break the one promise this screen makes. They go up in
   * `start`, between the session existing and the message being sent, so the
   * first turn sees them exactly as it would in an ordinary conversation.
   */
  const [files, setFiles] = useState<File[]>([]);
  /**
   * Thumbnails for the pictures among them, kept beside the files rather than
   * made while rendering: a fresh object URL on every render is a new one to
   * revoke on every render, and revoking none of them holds every file in
   * memory for as long as the screen is open.
   */
  const previews = useRef(new Map<File, string>());
  useEffect(() => {
    for (const [file, url] of previews.current) {
      if (!files.includes(file)) { URL.revokeObjectURL(url); previews.current.delete(file); }
    }
  }, [files]);
  useEffect(() => () => {
    for (const url of previews.current.values()) URL.revokeObjectURL(url);
    previews.current.clear();
  }, []);

  const attached: Attached[] = files.map((f, i) => {
    if (f.type.startsWith('image/') && !previews.current.has(f)) {
      previews.current.set(f, URL.createObjectURL(f));
    }
    const preview = previews.current.get(f);
    return {
      id: `local-${i}-${f.name}`, name: f.name, bytes: f.size,
      kind: f.type.startsWith('image/') ? 'image' as const : 'other' as const,
      ...(preview ? { preview } : {}),
    };
  });

  /**
   * One message, one session.
   *
   * Ordered so the first turn is governed by what was chosen on this screen:
   * the mode and the workspace are both in place before the message goes. A
   * send that raced them would leave the very first tool call gated by the
   * wrong policy, which is the one turn where that matters most.
   */
  const start = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setSending(true);
    setProblem(undefined);
    // Started with the animation, awaited after the work: whichever is slower
    // is what the person waits for, and neither is waited for twice.
    const settled = new Promise((done) => setTimeout(done, SLIDE_MS));
    try {
      const session = await client.createSession({
        ...(model ? { model } : {}),
      });

      // Before the message, not after. A mode applied afterwards would arrive
      // one turn late. Every chosen mode is sent, Manual included: it was
      // skipped as the default, and the session then ran on the person's own
      // ceiling while the screen said it would ask before every change.
      if (mode) {
        await client.setSessionPermissionMode(session.id, mode);
      }
      if (place.workspaceIds.length) {
        await client.attachWorkspace(session.id, place.workspaceIds);
      }
      for (const file of files) await client.attach(session.id, file);

      // Held until the box has finished travelling, so the conversation's own
      // composer is drawn where this one already is rather than the two
      // swapping places mid-flight. The work usually takes longer than the
      // animation, in which case this waits for nothing.
      await settled;

      // Navigate, then send. The conversation is on screen while the model is
      // thinking rather than after it has answered.
      onCreated(session.id);
      await client.send(session.id, text);
    } catch (err) {
      // The draft is still in the box, and the greeting comes back. Whatever
      // went wrong, the thing they typed is the last thing that should be lost
      // to it.
      setProblem((err as Error).message);
      setSending(false);
      setBusy(false);
    }
  };

  return (
    /**
     * The composer starts in the middle of the screen and travels to the
     * bottom as the first message goes.
     *
     * Two flexible spacers above and below it: equal, so it sits centred; the
     * lower one collapsing to nothing, so it arrives exactly where the
     * conversation's own composer lives. That is what makes the handover
     * invisible — by the time the session exists and this screen is replaced,
     * the box is already where the next one will be drawn.
     *
     * The greeting collapses on the same curve rather than fading in place,
     * or the box would stop short of the bottom by the height of a heading
     * nobody can see any more.
     */
    <main className="c42-main c42-draft" data-sending={sending}>
      <div className="c42-draftspace" />

      <div className="c42-greetwrap">
        <div className="c42-greet">
          {/* The product's own mark, not a decorative glyph. This is the one
              screen with room for it, and it is the screen somebody lands on. */}
          <span className="c42-greetmark" aria-hidden="true">42</span>
          <h1>{greeting(state.me?.name)}</h1>
        </div>
      </div>

      <Composer
        autoFocus
        draft={draft} onDraft={setDraft} onSend={() => void start()}
        busy={busy}
        placeholder="Describe a task or ask a question"
        attached={attached}
        onFiles={(picked) => setFiles((f) => [...f, ...picked])}
        onRemove={(id) => setFiles((f) => f.filter((_, i) => !id.startsWith(`local-${i}-`)))}
        {...(problem ? { problem } : {})}
        controls={
          <ChipBar
            mode={mode} onMode={setMode}
            model={model} onModel={setModel}
            place={place} onPlace={setPlace}
          />
        }
      />

      <div className="c42-draftspace bottom" />
    </main>
  );
}

/**
 * The time of day, and their name if we know it.
 *
 * The name comes from the identity token's own `name` claim, never derived
 * from an address: "jodypaterson@gmail.com" would give "Jodypaterson", and
 * greeting somebody by a mangled version of their email is worse than not
 * greeting them at all. Without a name it is still a greeting, just a general
 * one.
 */
export function greeting(name?: string, at: Date = new Date()): string {
  const hour = at.getHours();
  const part = hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening';
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : `Good ${part.toLowerCase()}`;
}

/**
 * A keyboard shortcut for the thing people do most.
 *
 * Cmd+N, as everywhere else. Its own hook so the shell can own the state and
 * this file can own the binding.
 */
export function useNewSessionKey(onNew: () => void): void {
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        onNew();
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onNew]);
}
